import {Box, HStack, useElementSize} from 'platform/foundation';
import {match} from 'ts-pattern';

import {ReactNode, useState} from 'react';

import {isNil} from 'ramda';

type RowFit = {isCompact: false} | {isCompact: true; requiredWidth: number};

const EXPANDED_FIT: RowFit = {isCompact: false};

// Matches HStack spacing={2} between the segment and the actions.
const ROW_GAP_PX = 8;
// A margin on both sides of the fitting width, so sub-pixel changes cannot toggle the layout.
const FIT_TOLERANCE_PX = 1;

const getNextRowFit = (
  fit: RowFit,
  rowWidth: number | null,
  segmentWidth: number | null,
  actionsWidth: number | null
): RowFit => {
  if (isNil(rowWidth) || isNil(segmentWidth) || isNil(actionsWidth)) {
    return fit;
  }

  return match(fit)
    .with({isCompact: true}, (compactFit) =>
      rowWidth >= compactFit.requiredWidth + FIT_TOLERANCE_PX ? EXPANDED_FIT : fit
    )
    .otherwise(() => {
      const requiredWidth = segmentWidth + ROW_GAP_PX + actionsWidth;
      return requiredWidth > rowWidth + FIT_TOLERANCE_PX ? {isCompact: true, requiredWidth} : fit;
    });
};

interface RequestTabSegmentActionsRowProps {
  segment: ReactNode;
  /** Compact actions move the less used ones into a menu, so the row fits its width. */
  renderActions: (isCompact: boolean) => ReactNode;
}

/**
 * The request tab segment with the work-item actions on its right, like the service order job
 * card (JobSegmentActionsRow in workshop; modules cannot import each other). When the actions do
 * not fit next to the segment, the row renders them compact, and it renders them in full again
 * once the row is as wide as the full actions needed. When even the compact actions do not fit,
 * they wrap below the segment.
 */
export function RequestTabSegmentActionsRow(props: RequestTabSegmentActionsRowProps) {
  const [rowRef, rowWidth] = useElementSize<HTMLDivElement>();
  const [segmentRef, segmentWidth] = useElementSize<HTMLDivElement>();
  const [actionsRef, actionsWidth] = useElementSize<HTMLDivElement>();
  const [fit, setFit] = useState<RowFit>(EXPANDED_FIT);

  // The measured widths decide the layout, so the state follows them while rendering.
  const nextFit = getNextRowFit(fit, rowWidth, segmentWidth, actionsWidth);
  if (nextFit !== fit) {
    setFit(nextFit);
  }

  return (
    <Box ref={rowRef}>
      {/* Wraps when even the compact actions do not fit, so they move under the segment. */}
      <HStack justify="space-between" align="center" spacing={2} wrap>
        <Box ref={segmentRef} flexShrink={0}>
          {props.segment}
        </Box>
        <Box ref={actionsRef} flexShrink={0}>
          {props.renderActions(fit.isCompact)}
        </Box>
      </HStack>
    </Box>
  );
}
